import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  ComposedChart,
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { RotateCcw, ChevronDown } from "lucide-react";
import {
  buildCentrifugeFlat,
  decimateSeries,
  aggregateWindow,
  GRANULARITY_STEPS,
} from "utils/centrifuge";
import { useTranslation } from "react-i18next";

// Per-axis series colours (shared with the export waveform chart).
const AXES = [
  { key: "x", label: "X", color: "#6366F1" },
  { key: "y", label: "Y", color: "#10B981" },
  { key: "z", label: "Z", color: "#E07A3A" },
  { key: "mag", label: "|a|", color: "#111827" },
];

// Auto granularity aims for a few bins per horizontal pixel; the render is
// capped so a wide "Raw" window can't dump 200k points into the chart.
const BINS_PER_PIXEL = 3;
const MAX_RENDER_POINTS = 4000;
const GRAN_STORAGE_KEY = "sit.centrifuge.granularity";

const fmtT = (s) => (s == null ? "" : `${Number(s).toFixed(3)}s`);
const fmtG = (v) => (typeof v === "number" ? v.toFixed(2) : v);
const fmtAxisT = (v) => {
  const n = Number(v);
  return Number.isInteger(n) ? `${n}s` : `${n.toFixed(n < 1 ? 2 : 1)}s`;
};
const fmtBinMs = (ms) =>
  ms >= 1000
    ? `${(ms / 1000).toFixed(ms % 1000 === 0 ? 0 : 1)} s`
    : `${ms < 1 ? ms.toFixed(1) : Math.round(ms)} ms`;

const DEFAULT_SETTING = { bin: "auto", stat: "avg" };

// Per-band { bin, stat }, persisted. Tolerates the older string-only shape.
const loadSettings = () => {
  const norm = (x) =>
    typeof x === "string"
      ? { bin: x || "auto", stat: "avg" }
      : {
          bin: x?.bin || "auto",
          stat: x?.stat === "max" ? "max" : "avg",
        };
  try {
    const v =
      JSON.parse(localStorage.getItem(GRAN_STORAGE_KEY) || "null") || {};
    return { low: norm(v.low), high: norm(v.high) };
  } catch {
    return { low: { ...DEFAULT_SETTING }, high: { ...DEFAULT_SETTING } };
  }
};

// Tooltip is honest about aggregation: an averaged/max view shows the chosen
// stat plus the bin's min/max and sample count, never a bin value dressed up as
// an instantaneous sample.
const AggTooltip = ({
  active,
  payload,
  label,
  shownAxes = [],
  aggregated,
  stat,
}) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const statKey = stat === "max" ? "max" : "mean";
  return (
    <div className="rounded-lg border border-[#F3F4F6] bg-white px-2.5 py-2 shadow-sm">
      <p className="mb-1 font-mono text-[10px] text-[#9CA3AF]">
        {fmtT(label)}
        {aggregated
          ? ` · ${t(
              stat === "max"
                ? "devices.waveform.stat.max"
                : "devices.waveform.stat.avg"
            )} · n=${d.n}`
          : ""}
      </p>
      {shownAxes.map((a) => (
        <div key={a.key} className="flex items-center gap-1.5">
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: a.color }}
          />
          <span className="text-[11px] text-[#374151]">{a.label}</span>
          <span className="ml-auto pl-3 font-mono text-[11px] text-[#111827]">
            {fmtG(d[`${a.key}_${statKey}`])} g
          </span>
          {aggregated && (
            <span className="font-mono text-[10px] text-[#9CA3AF]">
              ({fmtG(d[`${a.key}_min`])}/{fmtG(d[`${a.key}_max`])})
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Continuous, interactive waveform of an exported recording. All records are
 * concatenated per band. A per-band granularity control aggregates the in-view
 * window into mean/min/max bins (mean line + translucent min–max envelope), so
 * an operator can flip between "the true level" (smoothed) and "what the rig is
 * doing" (raw). Drag to zoom, double-click to reset; a slim overview strip shows
 * the whole trace with the window shaded.
 */
const WaveformGraph = ({
  records,
  initialBand = "low",
  // Optional video-sync integration: a vertical playhead at this data-time
  // (seconds), and a callback when the operator clicks (not drags) the trace.
  playheadT = null,
  onTraceClick = null,
}) => {
  const { t } = useTranslation();
  const [band, setBand] = useState(initialBand);
  const [visibleAxes, setVisibleAxes] = useState({
    x: true,
    y: true,
    z: true,
    mag: true,
  });
  const [zoom, setZoom] = useState(null); // { tStart, tEnd } | null (whole trace)
  const [selLeft, setSelLeft] = useState(null);
  const [selRight, setSelRight] = useState(null);
  const [selSource, setSelSource] = useState(null); // "main" | "overview"

  // Per-band { bin, stat }, persisted across reloads.
  const [settings, setSettings] = useState(loadSettings);
  useEffect(() => {
    try {
      localStorage.setItem(GRAN_STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* ignore quota / private mode */
    }
  }, [settings]);
  const cur = settings[band] || DEFAULT_SETTING;
  const granSel = cur.bin;
  const stat = cur.stat;
  const setBin = (bin) =>
    setSettings((s) => ({ ...s, [band]: { ...s[band], bin } }));
  const setStat = (st) =>
    setSettings((s) => ({ ...s, [band]: { ...s[band], stat: st } }));

  // Measure the plot width so Auto can size bins to ~pixels.
  const plotRef = useRef(null);
  const [plotW, setPlotW] = useState(800);
  useEffect(() => {
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setPlotW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const lowFlat = useMemo(() => buildCentrifugeFlat(records, "low"), [records]);
  const highFlat = useMemo(
    () => buildCentrifugeFlat(records, "high"),
    [records]
  );
  const flat = band === "high" ? highFlat : lowFlat;
  const rate = flat.rateHz || 960;

  // A different band is a different time base; drop the zoom.
  useEffect(() => {
    setZoom(null);
    setSelLeft(null);
    setSelRight(null);
  }, [band]);

  const fullDomain = [0, flat.durationS || 0];
  const xDomain = zoom ? [zoom.tStart, zoom.tEnd] : fullDomain;
  const windowDurS = zoom ? zoom.tEnd - zoom.tStart : flat.durationS || 0;
  const windowSamples = Math.max(1, Math.round(windowDurS * rate));

  // Bin width in raw samples for the current band + zoom + granularity choice.
  const { binSamples, rawCapped } = useMemo(() => {
    const step = GRANULARITY_STEPS.find((s) => s.id === granSel);
    let bs;
    if (!step || step.binMs == null) {
      // Auto: a few bins per pixel across the visible window.
      const targetBins = Math.max(1, Math.floor(plotW * BINS_PER_PIXEL));
      bs = Math.ceil(windowSamples / targetBins);
    } else if (step.binMs === 0) {
      bs = 1; // raw
    } else {
      bs = Math.round((step.binMs / 1000) * rate);
    }
    bs = Math.max(1, bs);
    const wantedRaw = bs <= 1;
    // Cap the point count so a wide raw/fine window stays fast.
    if (Math.ceil(windowSamples / bs) > MAX_RENDER_POINTS) {
      bs = Math.ceil(windowSamples / MAX_RENDER_POINTS);
    }
    return { binSamples: bs, rawCapped: wantedRaw && bs > 1 };
  }, [granSel, windowSamples, plotW, rate]);

  const view = useMemo(
    () =>
      aggregateWindow(flat.flat, {
        tStart: zoom?.tStart ?? null,
        tEnd: zoom?.tEnd ?? null,
        binSamples,
      }),
    [flat, zoom, binSamples]
  );

  const aggregated = !view.raw;
  const effBinMs = (binSamples / rate) * 1000;

  // Overview: whole trace, coarse peak-preserving, purely a navigator.
  const overview = useMemo(
    () => decimateSeries(flat.flat, { maxPoints: 700 }),
    [flat]
  );

  const bands = [
    { id: "low", label: t("devices.waveform.lowG"), n: lowFlat.totalSamples },
    {
      id: "high",
      label: t("devices.waveform.highG"),
      n: highFlat.totalSamples,
    },
  ];
  const shownAxes = AXES.filter((a) => visibleAxes[a.key]);

  // Y auto-rescales to the envelope extremes in view.
  const yDomain = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const p of view.points) {
      for (const a of shownAxes) {
        const lo = p[`${a.key}_min`];
        const hi = p[`${a.key}_max`];
        if (lo != null && lo < min) min = lo;
        if (hi != null && hi > max) max = hi;
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [-1, 1];
    if (min === max) return [min - 1, max + 1];
    const pad = (max - min) * 0.08;
    return [
      Math.floor((min - pad) * 10) / 10,
      Math.ceil((max + pad) * 10) / 10,
    ];
    // eslint-disable-next-line
  }, [view.points, visibleAxes]);

  // ── Drag-to-zoom (main chart + overview) ──
  const onDown = (source) => (e) => {
    if (e?.activeLabel == null) return;
    setSelSource(source);
    setSelLeft(e.activeLabel);
    setSelRight(e.activeLabel);
  };
  const onMove = (e) => {
    if (selLeft == null || e?.activeLabel == null) return;
    setSelRight(e.activeLabel);
  };
  const clearSel = () => {
    setSelLeft(null);
    setSelRight(null);
    setSelSource(null);
  };
  const commitZoom = (source) => () => {
    if (selLeft != null && selRight != null && selLeft !== selRight) {
      const a = Math.min(selLeft, selRight);
      const b = Math.max(selLeft, selRight);
      const span =
        (source === "overview" ? flat.durationS : xDomain[1] - xDomain[0]) ||
        flat.durationS ||
        1;
      if (b - a > span * 0.005) {
        setZoom({ tStart: a, tEnd: b });
        clearSel();
        return;
      }
    }
    // A click (no meaningful drag) on the main chart seeks a synced video.
    if (source === "main" && onTraceClick && selLeft != null) {
      onTraceClick(selLeft);
    }
    clearSel();
  };
  const resetZoom = () => {
    setZoom(null);
    clearSel();
  };

  const zoomed = !!zoom;
  const selecting = selLeft != null && selRight != null && selLeft !== selRight;
  const selectingMain = selecting && selSource === "main";
  const selectingOverview = selecting && selSource === "overview";
  const statLabel = t(
    stat === "max" ? "devices.waveform.stat.max" : "devices.waveform.stat.avg"
  );

  return (
    <div className="select-none rounded-xl border border-[#F3F4F6] bg-white p-4">
      {/* Band tabs + legend */}
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-[10px] border border-[#E5E7EB] p-0.5">
          {bands.map((b) => {
            const on = band === b.id;
            return (
              <button
                key={b.id}
                onClick={() => setBand(b.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition duration-150 ${
                  on
                    ? "bg-[#FFF3ED] text-[#E07A3A]"
                    : "text-[#6B7280] hover:text-[#374151]"
                }`}
              >
                {b.label}
                <span className="ml-1.5 font-normal text-[#9CA3AF]">
                  {b.n.toLocaleString()}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          {AXES.map((a) => {
            const on = visibleAxes[a.key];
            return (
              <button
                key={a.key}
                onClick={() =>
                  setVisibleAxes((prev) => ({ ...prev, [a.key]: !prev[a.key] }))
                }
                className={`flex items-center gap-1.5 text-xs font-medium transition duration-150 ${
                  on ? "text-[#374151]" : "text-[#C4C8CE] line-through"
                }`}
              >
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: a.color, opacity: on ? 1 : 0.3 }}
                />
                {a.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail control — per band: stat (average / max) + bin size */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.07em] text-[#9CA3AF]">
          {t("devices.waveform.detail")}
        </span>
        <div className="inline-flex rounded-[10px] border border-[#E5E7EB] p-0.5">
          {[
            { id: "avg", label: t("devices.waveform.statButton.avg") },
            { id: "max", label: t("devices.waveform.statButton.max") },
          ].map((o) => {
            const on = stat === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setStat(o.id)}
                title={
                  o.id === "max"
                    ? t("devices.waveform.followsPeak")
                    : t("devices.waveform.followsAverage")
                }
                className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition duration-150 ${
                  on
                    ? "bg-[#FFF3ED] text-[#E07A3A]"
                    : "text-[#6B7280] hover:text-[#374151]"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <div className="relative">
          <select
            value={granSel}
            onChange={(e) => setBin(e.target.value)}
            title={t("devices.waveform.binWidth")}
            className="cursor-pointer appearance-none rounded-lg border border-[#E5E7EB] bg-white py-1.5 pl-2.5 pr-7 text-[11px] font-semibold text-[#374151] outline-none transition-all duration-150 focus:border-[#E07A3A] focus:ring-2 focus:ring-[#E07A3A]/10"
          >
            {GRANULARITY_STEPS.map((s) => (
              <option key={s.id} value={s.id}>
                {t(`devices.waveform.granularity.${s.id}`, {
                  defaultValue: s.label,
                })}
              </option>
            ))}
          </select>
          <ChevronDown
            size={13}
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
          />
        </div>
        <span className="text-[10px] text-[#B9BEC6]">
          {rawCapped
            ? t("devices.waveform.rawNeedsZoom", {
                stat: statLabel,
                bin: fmtBinMs(effBinMs),
              })
            : aggregated
            ? t("devices.waveform.aggregatedBins", {
                stat: statLabel,
                bin: fmtBinMs(effBinMs),
              })
            : t("devices.waveform.rawRate", { rate })}
        </span>
      </div>

      {/* Main chart */}
      <div ref={plotRef} className="h-[360px] cursor-crosshair">
        {view.points.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-[#9CA3AF]">
            {t("devices.waveform.noSamples")}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={view.points}
              margin={{ top: 8, right: 12, left: -8, bottom: 0 }}
              onMouseDown={onDown("main")}
              onMouseMove={onMove}
              onMouseUp={commitZoom("main")}
              onMouseLeave={clearSel}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#F3F4F6"
              />
              <XAxis
                dataKey="t"
                type="number"
                domain={xDomain}
                allowDataOverflow
                tickFormatter={fmtAxisT}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#9CA3AF" }}
              />
              <YAxis
                width={40}
                domain={yDomain}
                allowDataOverflow
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#9CA3AF" }}
                tickFormatter={(v) => `${v}g`}
              />
              <Tooltip
                content={
                  <AggTooltip
                    shownAxes={shownAxes}
                    aggregated={aggregated}
                    stat={stat}
                  />
                }
                isAnimationActive={false}
              />
              {/* min–max envelope behind the mean line (aggregated views only) */}
              {aggregated &&
                shownAxes.map((a) => (
                  <Area
                    key={`${a.key}-band`}
                    type="monotone"
                    dataKey={`${a.key}_range`}
                    stroke="none"
                    fill={a.color}
                    fillOpacity={0.15}
                    isAnimationActive={false}
                    activeDot={false}
                    dot={false}
                  />
                ))}
              {shownAxes.map((a) => (
                <Line
                  key={a.key}
                  type="monotone"
                  dataKey={`${a.key}_${stat === "max" ? "max" : "mean"}`}
                  stroke={a.color}
                  strokeWidth={a.key === "mag" ? 1.75 : 1.1}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
              {playheadT != null && (
                <ReferenceLine
                  x={playheadT}
                  stroke="#DC2626"
                  strokeWidth={1.5}
                  ifOverflow="hidden"
                  isFront
                />
              )}
              {selectingMain && (
                <ReferenceArea
                  x1={selLeft}
                  x2={selRight}
                  strokeOpacity={0}
                  fill="#E07A3A"
                  fillOpacity={0.12}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Overview strip — the whole trace as a navigator, set apart from the
          main plot. Mirrors the shown axes, with the in-view window shaded. */}
      {overview.points.length > 1 && (
        <div className="mt-4 rounded-lg bg-[#FAFAF8] px-2 pb-1 pt-1.5 ring-1 ring-inset ring-[#ECEAE6]">
          <div className="flex items-center justify-between px-1 pb-0.5">
            <span className="text-[9px] font-semibold uppercase tracking-[0.09em] text-[#B9BEC6]">
              {t("devices.waveform.fullRecording")}
            </span>
            <span className="text-[9px] font-medium text-[#C4C8CE]">
              {t("devices.waveform.dragToZoom")}
            </span>
          </div>
          <div className="h-[40px] cursor-crosshair">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={overview.points}
                margin={{ top: 2, right: 12, left: -8, bottom: 0 }}
                onMouseDown={onDown("overview")}
                onMouseMove={onMove}
                onMouseUp={commitZoom("overview")}
                onMouseLeave={clearSel}
              >
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={fullDomain}
                  allowDataOverflow
                  hide
                />
                <YAxis hide domain={["dataMin", "dataMax"]} />
                {shownAxes.map((a) => (
                  <Line
                    key={a.key}
                    type="monotone"
                    dataKey={a.key}
                    stroke={a.color}
                    strokeOpacity={0.55}
                    strokeWidth={1}
                    dot={false}
                    isAnimationActive={false}
                  />
                ))}
                {zoomed && !selectingOverview && (
                  <ReferenceArea
                    x1={zoom.tStart}
                    x2={zoom.tEnd}
                    strokeOpacity={0}
                    fill="#E07A3A"
                    fillOpacity={0.16}
                  />
                )}
                {selectingOverview && (
                  <ReferenceArea
                    x1={selLeft}
                    x2={selRight}
                    strokeOpacity={0}
                    fill="#E07A3A"
                    fillOpacity={0.16}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Caption + reset */}
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-[11px] text-[#9CA3AF]">
          {zoomed ? (
            <>
              {t("devices.waveform.window", {
                start: fmtAxisT(zoom.tStart),
                end: fmtAxisT(zoom.tEnd),
                samples: view.windowSamples.toLocaleString(),
              })}
            </>
          ) : (
            <>
              {t("devices.waveform.durationAtRate", {
                duration: Math.round(flat.durationS),
                rate,
              })}
              {flat.fsG != null
                ? t("devices.waveform.fullScale", { fs: flat.fsG })
                : ""}
            </>
          )}
          {aggregated
            ? t("devices.waveform.binsSuffix", {
                stat: statLabel,
                bin: fmtBinMs(effBinMs),
              })
            : ""}
        </p>
        {zoomed && (
          <button
            onClick={resetZoom}
            className="flex items-center gap-1 rounded-md border border-[#E5E7EB] bg-white px-2 py-1 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
          >
            <RotateCcw size={11} />
            {t("devices.waveform.resetZoom")}
          </button>
        )}
      </div>
    </div>
  );
};

export default WaveformGraph;
